import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import {
  useIncidentDetail,
  useIncidentAppleEedCapabilities,
  useIncidentLiveState,
  usePortalHub,
} from '@ngsos/portal-core/portal';

import { LoadingFailedBox, NgPageHeader } from '@/ui';
import { useAgencyConfiguration } from '@/app/shell/agencyConfiguration';
import {
  canUseAppleEedVideoActions,
  getCollaborationState,
  getQueryParamCaseInsensitive,
  isAppleEedLiveVideo,
} from '@ngsos/portal-core/incidents';
import { CollaborationState, isCoordinateLocation } from '@/signalr';
import { openPhoneNumberDialog } from './actions';
import { IncidentDetailDialogs } from './dialogs/IncidentDetailDialogs';
import { createIncidentRealtime, IncidentRealtimeContext } from '@ngsos/portal-core/chat';
import { ActionsMobileMenuProvider } from './layout/actionsMobileMenu';
import { IncidentDetailLayoutBox } from './layout/IncidentDetailLayoutBox';
import { getInitialIncidentPanel } from './layout/incidentPanel';
import { IncidentBanner } from './banners/IncidentBanner';
import { IncidentDetailCollaborationStateBanner } from './banners/IncidentDetailCollaborationStateBanner';
import { IncidentDataPanel } from './IncidentDataPanel';
import { IncidentDetailActionBar } from './IncidentDetailActionBar';
import { IncidentDetailMap } from './IncidentDetailMap';
import { IncidentNotFoundPage } from './IncidentNotFoundPage';
import {
  IncidentDetailMapLoadingPlaceholder,
  IncidentDetailPanelLoadingPlaceholder,
} from './IncidentDetailLoadingPlaceholders';
import { IncidentChatPlaceholder } from './placeholders/IncidentChatPlaceholder';
import { VideoBoxPlaceholder } from './placeholders/VideoBoxPlaceholder';

export type IncidentDetailPageProps = {
  incidentId: string;
};

export function IncidentDetailPage({ incidentId }: IncidentDetailPageProps) {
  const { t } = useTranslation();
  const agencyConfiguration = useAgencyConfiguration();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const hub = usePortalHub();
  const { incident, status, retry } = useIncidentDetail(incidentId);
  const { callerConnectedStatus } = useIncidentLiveState(incidentId);

  const realtime = useMemo(() => (hub === null ? null : createIncidentRealtime(hub, incidentId)), [hub, incidentId]);

  const callerPhoneNumber = incident?.caller.phoneNumber ?? null;
  const appleEedCapabilities = useIncidentAppleEedCapabilities(incidentId);

  const isLoading = incident === null;

  const incidentIsClosed = incident?.isClosed === true;
  const callerCurrentLocation = incident?.caller.currentLocation ?? null;
  const lastCallerCoordinate =
    callerCurrentLocation !== null && isCoordinateLocation(callerCurrentLocation) ? callerCurrentLocation : null;
  const hasPhoneNumber = callerPhoneNumber !== null;

  const incidentCollaborationState =
    incident !== null ? getCollaborationState(incident, agencyConfiguration.id) : null;
  const incidentIsReadOnly =
    isLoading || incidentIsClosed || incidentCollaborationState === CollaborationState.Declined;
  const allowAppleEedVideoActions = canUseAppleEedVideoActions(
    agencyConfiguration.appleEedAllowLiveVideo,
    incidentIsReadOnly,
  );

  const liveVideos = incident?.caller.liveVideos ?? [];
  const activeLiveVideo = liveVideos.find((video) => video.id === incident?.activeLiveVideoId) ?? null;
  const hasVideoPanel = !incidentIsReadOnly || liveVideos.length > 0;
  const activeAppleEedVideo = isAppleEedLiveVideo(activeLiveVideo) ? activeLiveVideo : null;
  const hasActiveAppleEedVideo = activeAppleEedVideo !== null;
  const hasAppleEedVideoCapability = appleEedCapabilities?.liveVideo ?? false;
  const appleEedVideoCallId = activeAppleEedVideo?.callerPhoneNumber ?? null;
  const initialActivePanel = incident === null ? null : getInitialIncidentPanel(activeLiveVideo !== null);

  const [collapsedVideoId, setCollapsedVideoId] = useState<string | null>(null);
  const videoExpanded =
    activeLiveVideo !== null && !incidentIsReadOnly && collapsedVideoId !== activeLiveVideo.id;

  const onVideoExpandedChanged = (expanded: boolean) => {
    setCollapsedVideoId(expanded ? null : (activeLiveVideo?.id ?? null));
  };

  const phoneNumberDialogHandledRef = useRef(false);
  const openPhoneNumberDialogParam = getQueryParamCaseInsensitive(searchParams, 'OpenPhoneNumberDialog');
  useEffect(() => {
    if (isLoading || phoneNumberDialogHandledRef.current) {
      return;
    }
    if (openPhoneNumberDialogParam?.toLowerCase() === 'true') {
      phoneNumberDialogHandledRef.current = true;
      openPhoneNumberDialog(incidentId);
      void navigate(`/incident/${incidentId}`);
    }
  }, [isLoading, openPhoneNumberDialogParam, incidentId, navigate]);

  if (status === 'not-found') {
    return <IncidentNotFoundPage />;
  }

  if (status === 'failed') {
    return (
      <div className="content p-3">
        <NgPageHeader title={t('Incident_PageTitle')} />
        <LoadingFailedBox onRetry={retry} />
      </div>
    );
  }

  return (
    <>
      <NgPageHeader title={t('Incident_PageTitle')} />

      <IncidentRealtimeContext.Provider value={realtime}>
        <ActionsMobileMenuProvider>
          <IncidentDetailLayoutBox
            incidentIsClosed={incidentIsClosed}
            initialActivePanel={initialActivePanel}
            hasVideoPanel={hasVideoPanel}
            hasActiveAppleEedVideo={hasActiveAppleEedVideo}
            videoExpanded={videoExpanded}
            dataPanelArea={
              isLoading ? (
                <IncidentDetailPanelLoadingPlaceholder />
              ) : incident !== null ? (
                <IncidentDataPanel
                  incident={incident}
                  incidentIsReadOnly={incidentIsReadOnly}
                  appleEedCapabilities={appleEedCapabilities}
                />
              ) : null
            }
            bannersArea={
              <>
                {incidentIsClosed ? (
                  <IncidentBanner>{t('Incident_IncidentClosedStatusLabel')}</IncidentBanner>
                ) : (
                  incident !== null && (
                    <IncidentDetailCollaborationStateBanner
                      incidentId={incidentId}
                      collaborationState={incidentCollaborationState}
                    />
                  )
                )}
              </>
            }
            actionBarArea={
              !isLoading && (
                <IncidentDetailActionBar
                  incidentIsReadOnly={incidentIsReadOnly}
                  incidentId={incidentId}
                />
              )
            }
            mapArea={
              isLoading ? (
                <IncidentDetailMapLoadingPlaceholder />
              ) : (
                <IncidentDetailMap
                  incidentId={incidentId}
                  callerPosition={callerCurrentLocation}
                  callerPositionHistory={incident?.caller.currentTrack ?? []}
                  lastLocationBySource={incident?.caller.lastLocationBySource ?? []}
                  permanentResidence={incident?.caller.permanentResidence}
                  incidentIsReadOnly={incidentIsReadOnly}
                />
              )
            }
            videoPanelArea={
              <VideoBoxPlaceholder
                incidentId={incidentId}
                incidentIsClosed={incidentIsClosed}
                incidentIsReadOnly={incidentIsReadOnly}
                incidentHasPhoneNumber={hasPhoneNumber}
                hasAppleEedVideoCapability={hasAppleEedVideoCapability}
                allowAppleEedVideoActions={allowAppleEedVideoActions}
                appleEedVideoCallId={appleEedVideoCallId}
                liveVideos={liveVideos}
                activeLiveVideoId={incident?.activeLiveVideoId ?? null}
                videoExpanded={videoExpanded}
                onVideoExpandedChanged={onVideoExpandedChanged}
                callerConnectedStatus={callerConnectedStatus}
              />
            }
            chatArea={
              <IncidentChatPlaceholder
                incidentId={incidentId}
                callerConnectedStatus={callerConnectedStatus}
                incidentHasPhoneNumber={hasPhoneNumber}
                incidentIsReadOnly={incidentIsReadOnly}
                hasActiveAppleEedVideo={hasActiveAppleEedVideo}
                videoExpanded={videoExpanded}
                onRequestVideoCollapse={() => setCollapsedVideoId(activeLiveVideo?.id ?? null)}
              />
            }
          />

          <IncidentDetailDialogs
            incidentId={incidentId}
            lastCallerPosition={lastCallerCoordinate}
          />
        </ActionsMobileMenuProvider>
      </IncidentRealtimeContext.Provider>
    </>
  );
}
